import React, { useState, useReducer, useMemo, useEffect } from 'react'
import Box from '@mui/material/Box'
import Divider from '@mui/material/Divider'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import GlobalStyles from '@mui/material/GlobalStyles'
import { DataGrid, GridRowsProp, GridColDef } from '@mui/x-data-grid'
import {
  useGetManagedReportsQuery,
  useGetPagedReportsQuery,
  useGetReportsPageCountQuery,
  useGetReportsTaggingsQuery,
  useGetReportYearsQuery,
} from 'gqlTypes'
import { useNavigate } from 'react-router-dom'
import Navbar from './Navbar'
import { SIDEBAR_WIDTH } from './CommonSidebar'
import {
  ReportFilters,
  filterReducer,
  initFilters,
  filterReports,
} from './ReportFilters'
import { Link } from 'react-router-dom'
import { Button, LinearProgress, Tooltip } from '@mui/material'
import ShareDialog from './ShareDialog'
import { getReportTypeTag, getYearTag } from 'util/getReportTags'

const ReportsPage: React.FC = () => {
  const [pageSize, setPageSize] = useState(10)
  const [currentPage, setCurrentPage] = useState(1)
  const [shareOpen, setShareOpen] = useState(false)
  const [shareReports, setShareReports] = useState<GridRowsProp>([])
  const perPage = 500

  const { data: countData, loading: countLoading } =
    useGetReportsPageCountQuery({
      variables: {
        perPage: perPage,
      },
    })

  const { data, loading, error, fetchMore } = useGetPagedReportsQuery({
    variables: { page: 1, perPage: perPage },
  })

  const { data: filterData, loading: filterLoading } =
    useGetReportsTaggingsQuery()

  const { data: managementData, loading: managementLoading } =
    useGetManagedReportsQuery()

  const [filters, filterDispatch] = useReducer(
    filterReducer,
    undefined,
    initFilters
  )

  useEffect(() => {
    if (error) {
      console.log('reports error', error)
    }
  }, [error])

  useEffect(() => {
    if (!countLoading && !loading) {
      setCurrentPage(currentPage + 1)
    }
  }, [data, loading, fetchMore, countData, countLoading])

  useEffect(() => {
    if (currentPage <= (countData?.reportsPageCount.totalPages || 0)) {
      fetchMore({ variables: { page: currentPage, perPage: perPage } })
    }
  }, [currentPage])

  const reports = useMemo(
    () =>
      (data &&
        filterReports(
          data.pagedReports,
          filters,
          filterData?.reportsTaggings || []
        )) ||
      [],
    [data, filters, filterData, currentPage]
  )

  const handleCloseShare = () => {
    setShareOpen(false)
    setShareReports([])
  }

  const rows = useMemo(() => {
    return (
      reports.map((r) => ({
        id: r.id,
        title: r.title,
        reportType: getReportTypeTag(r.id, filterData?.reportsTaggings || []),
        year: getYearTag(r.id, filterData?.reportsTaggings || []),
        share: (managementData?.managedReports || []).some(
          (d) => d.reportId === r.id
        ),
      })) ?? []
    )
  }, [reports, filterData, managementData])
  const shareCount = rows.filter((r) => r.share).length

  const columns: GridColDef[] = [
    {
      field: 'title',
      headerName: 'Report Name',
      flex: 1,
      minWidth: 300,
      renderCell: (params) => {
        return (
          <Link
            style={{
              textDecoration: 'none',
              color: 'inherit',
            }}
            to={`/reports/${params.row.id}/`}
            title={params.row.title}
          >
            {params.row.title}
          </Link>
        )
      },
    },
    { field: 'reportType', headerName: 'Report Type', width: 200 },
    { field: 'year', headerName: 'Year', width: 200 },
    {
      field: 'share',
      headerName: '',
      width: 100,
      renderCell: (params) => {
        if (params.row.share) {
          return (
            <Button
              onClick={() => {
                setShareReports([params.row])
                setShareOpen(true)
              }}
            >
              Share
            </Button>
          )
        }
        return ''
      },
      sortingOrder: ['desc', null],
    },
  ]

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#fff' } }} />
      <Navbar />
      <Box display="flex">
        <ReportFilters
          reports={data?.pagedReports || []}
          filters={filters}
          dispatch={filterDispatch}
          reportTaggings={filterData?.reportsTaggings || []}
        />
        <Box
          sx={{
            position: 'fixed',
            background: '#f6f6f6',
            width: 15,
            top: 0,
            bottom: 0,
            left: SIDEBAR_WIDTH,
          }}
        />
        <Box sx={{ minWidth: 15 }} />
        <Box sx={{ mx: 2, p: 6, width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h2" component="h1">
              My Reports
            </Typography>
            {data && (
              <Typography
                component="h1"
                variant="h4"
                sx={{ color: 'primary.light' }}
              >
                ({data.pagedReports.length} total)
              </Typography>
            )}
          </Box>
          <Box my={3} mb={5} maxWidth={400}>
            <Divider sx={{ borderColor: 'grey.400' }} />
          </Box>
          {error && (
            <Box>
              There was an error loading your reports. Please reload the page
              and try again.
            </Box>
          )}
          {!error &&
            data?.pagedReports.length !==
              countData?.reportsPageCount.totalCount && (
              <Box>
                <Box>Loading...</Box>
                <LinearProgress
                  variant="determinate"
                  value={Math.round(
                    (reports.length /
                      (countData?.reportsPageCount.totalCount ||
                        reports.length)) *
                      100
                  )}
                />
                <Box sx={{ minWidth: 35 }}>
                  <Typography textAlign="right">{`${Math.round(
                    (reports.length /
                      (countData?.reportsPageCount.totalCount ||
                        reports.length)) *
                      100
                  )}%`}</Typography>
                </Box>
              </Box>
            )}
          {data?.pagedReports.length ===
            countData?.reportsPageCount.totalCount &&
            rows.length > 0 && (
              <Stack spacing={2}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignContent="center"
                >
                  <Typography>
                    Showing{' '}
                    <b>
                      {rows.length > pageSize ? pageSize : rows.length} of{' '}
                      {rows.length}
                    </b>{' '}
                    reports
                  </Typography>
                  <Box>
                    {shareCount > 0 && (
                      <Tooltip title="Refine reports to share by filtering.">
                        <Button
                          onClick={() => {
                            setShareReports(rows.filter((r) => r.share))
                            setShareOpen(true)
                          }}
                        >
                          {`Share All Reports (${shareCount})`}
                        </Button>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
                <DataGrid
                  disableSelectionOnClick
                  disableColumnMenu
                  autoHeight
                  rows={rows}
                  columns={columns}
                  pageSize={pageSize}
                  rowsPerPageOptions={[10, 25, 50]}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  initialState={
                    {
                      // sorting: {
                      //   sortModel: [{ field: 'title', sort: 'asc' }],
                      // },
                    }
                  }
                  sx={{
                    '& .MuiDataGrid-row': { cursor: 'pointer' },
                  }}
                />
              </Stack>
            )}
          <ShareDialog
            open={shareOpen}
            reports={shareReports}
            onClose={handleCloseShare}
          />
        </Box>
      </Box>
    </>
  )
}

export default ReportsPage
